"use client";

import { useState, useEffect } from "react";

export default function GroupPackagesTable() {
  const [conferences, setConferences] = useState([]);
  const [selectedConference, setSelectedConference] = useState("");
  const [loadingConferences, setLoadingConferences] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [error, setError] = useState(null);
  const [packages, setPackages] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    package_name: "",
    type: "",
    price_per_person: "",
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    package_name: "",
    type: "",
    price_per_person: "",
  });
  const [adding, setAdding] = useState(false);

  const CONFERENCES_URL = "https://eventscons.com/eventbackendnew/api/conferences/all";
  const GROUP_PACKAGE_BASE = "https://eventscons.com/eventbackendnew/api/group-packages";
  const GROUP_PACKAGE_BY_CONF =
    "https://eventscons.com/eventbackendnew/api/group-packages/conference";

  const getAuthToken = () => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("access_token")
      );
    }
    return null;
  };

  // Conferences list
  const fetchConferences = async () => {
    try {
      setLoadingConferences(true);
      const token = getAuthToken();

      const res = await fetch(CONFERENCES_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) throw new Error(`Failed to load conferences: ${res.status}`);

      const json = await res.json();
      const list = json.data || [];
      setConferences(list);

      if (list.length > 0 && !selectedConference) {
        setSelectedConference(list[0].id);
      }
    } catch (err) {
      console.error("Error fetching conferences:", err);
      setError(err.message);
    } finally {
      setLoadingConferences(false);
    }
  };

  // Group packages by conference
  const fetchGroupPackages = async () => {
    if (!selectedConference) {
      setPackages([]);
      return;
    }
    try {
      setLoadingPackages(true);
      setError(null);

      const token = getAuthToken();
      if (!token)
        throw new Error("Authentication token not found. Please login first.");

      const url = `${GROUP_PACKAGE_BY_CONF}/${selectedConference}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`Server error: ${res.status} ${res.statusText}`);

      const json = await res.json();
      const list = Array.isArray(json.data) ? json.data : [];
      setPackages(list);
    } catch (err) {
      console.error("Error fetching group packages:", err);
      setError(err.message);
    } finally {
      setLoadingPackages(false);
    }
  };

  useEffect(() => {
    fetchConferences();
  }, []);

  useEffect(() => {
    if (selectedConference) fetchGroupPackages();
  }, [selectedConference]);

  // ---------- EDIT / UPDATE ----------

  const openEditModal = async (id) => {
    try {
      setError(null);
      const token = getAuthToken();
      if (!token)
        throw new Error("Authentication token not found. Please login first.");

      const url = `${GROUP_PACKAGE_BASE}/${id}`; // Get Single
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`Failed to load package: ${res.status}`);

      const json = await res.json();
      const pkg = json.data;

      setEditingPackage(pkg);
      setFormData({
        package_name: pkg.package_name || "",
        type: pkg.type || "",
        price_per_person: String(pkg.price_per_person ?? ""),
      });
      setEditModalOpen(true);
    } catch (err) {
      console.error("Error getting single package:", err);
      setError(err.message);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingPackage(null);
    setFormData({
      package_name: "",
      type: "",
      price_per_person: "",
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdate = async () => {
    if (!editingPackage) return;
    try {
      setSaving(true);
      setError(null);

      const token = getAuthToken();
      if (!token)
        throw new Error("Authentication token not found. Please login first.");

      const payload = {
        package_name: formData.package_name,
        type: formData.type,
        price_per_person: Number(formData.price_per_person),
      };

      const url = `${GROUP_PACKAGE_BASE}/${editingPackage.id}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Failed to update package: ${res.status}`);

      const json = await res.json();
      const updated = json.data || { ...editingPackage, ...payload };

      setPackages((prev) =>
        prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
      );

      closeEditModal();
    } catch (err) {
      console.error("Error updating package:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---------- DELETE ----------

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this package?"
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(id);
      setError(null);

      const token = getAuthToken();
      if (!token)
        throw new Error("Authentication token not found. Please login first.");

      const url = `${GROUP_PACKAGE_BASE}/${id}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`Failed to delete package: ${res.status}`);

      setPackages((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Error deleting package:", err);
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // ---------- ADD ----------

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openAddModal = () => {
    if (!selectedConference) {
      alert("Please select a conference first.");
      return;
    }
    setAddFormData({
      package_name: "",
      type: "",
      price_per_person: "",
    });
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const handleAdd = async () => {
    if (!selectedConference) return;

    try {
      setAdding(true);
      setError(null);

      const token = getAuthToken();
      if (!token)
        throw new Error("Authentication token not found. Please login first.");

      const payload = {
        package_name: addFormData.package_name,
        type: addFormData.type,
        price_per_person: Number(addFormData.price_per_person),
      };

      const url = `${GROUP_PACKAGE_BY_CONF}/${selectedConference}`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Create package error body:", text);
        throw new Error(`Failed to create package: ${res.status}`);
      }

      const json = await res.json();
      const created = json.data || payload;

      setPackages((prev) => [...prev, created]);
      closeAddModal();
    } catch (err) {
      console.error("Error adding package:", err);
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  // ---------- Styles ----------

  const containerStyle = {
    padding: "24px",
    maxWidth: "1200px",
    width: "100%",
    margin: "0 auto",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
  };
  const cardStyle = {
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    overflow: "hidden",
  };
  const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
  const thStyle = {
    padding: "16px 20px",
    textAlign: "left",
    fontWeight: "600",
    color: "#64748b",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    backgroundColor: "#f1f5f9",
    borderBottom: "2px solid #e2e8f0",
  };
  const tdStyle = {
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "middle",
  };
  const btnStyle = {
    padding: "6px 10px",
    background: "linear-gradient(135deg, #9B1321 0%, #9B1321 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontWeight: "500",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginRight: "6px",
  };
  const btnDanger = {
    ...btnStyle,
    background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
  };

  const modalOverlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };
  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    width: "100%",
    maxWidth: "480px",
    boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  };
  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    marginBottom: "12px",
    fontSize: "14px",
  };

  if (loadingPackages && !loadingConferences) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #9B1321",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "20px",
            }}
          />
          <span style={{ fontSize: "18px", color: "#64748b" }}>
            Loading group packages...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        tr:hover {
          background-color: #f8fafc !important;
        }
        button:hover {
          transform: translateY(-1px) !important;
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "32px",
              fontWeight: "800",
              background:
                "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              margin: 0,
            }}
          >
            Group Packages ({packages.length})
          </h2>
          <p
            style={{
              margin: "8px 0 0 0",
              color: "#64748b",
              fontSize: "14px",
            }}
          >
            {selectedConference
              ? `Conference ID: ${selectedConference}`
              : "Please select a conference to view group packages"}
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <select
            value={selectedConference}
            onChange={(e) => setSelectedConference(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #cbd5f5",
              minWidth: "220px",
              fontSize: "14px",
              outline: "none",
            }}
          >
            <option value="">
              {loadingConferences
                ? "Loading conferences..."
                : "Select Conference"}
            </option>
            {conferences.map((conf) => (
              <option key={conf.id} value={conf.id}>
                {conf.title || conf.name || `Conference ${conf.id}`}
              </option>
            ))}
          </select>

          <button
            onClick={fetchGroupPackages}
            style={btnStyle}
            disabled={!selectedConference}
          >
            🔄 Refresh
          </button>

          <button
            onClick={openAddModal}
            style={{
              ...btnStyle,
              background:
                "linear-gradient(135deg, #16a34a 0%, #16a34a 100%)",
            }}
            disabled={!selectedConference}
          >
            ➕ Add Package
          </button>
        </div>
      </div>

      {/* Error inline */}
      {error && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px 16px",
            borderRadius: "8px",
            backgroundColor: "#fef2f2",
            color: "#b91c1c",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}

      {/* Table / empty */}
      {packages.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "80px 40px",
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              fontSize: "64px",
              marginBottom: "20px",
              opacity: 0.3,
            }}
          >
            📭
          </div>
          <p
            style={{
              fontSize: "20px",
              color: "#64748b",
              marginBottom: "24px",
            }}
          >
            {selectedConference
              ? "No group packages found for this conference"
              : "Please select a conference to view group packages"}
          </p>
          {selectedConference && (
            <button onClick={fetchGroupPackages} style={btnStyle}>
              Reload Data
            </button>
          )}
        </div>
      ) : (
        <div style={{ ...cardStyle, overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Package Name</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Price Per Person</th>
                <th style={thStyle}>Updated At</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((pkg) => (
                <tr key={pkg.id}>
                  <td style={tdStyle}>{pkg.id}</td>
                  <td style={tdStyle}>{pkg.package_name}</td>
                  <td style={tdStyle}>{pkg.type}</td>
                  <td style={tdStyle}>{pkg.price_per_person}</td>
                  <td style={tdStyle}>
                    {pkg.updated_at
                      ? new Date(pkg.updated_at).toLocaleString()
                      : "-"}
                  </td>
                  <td style={tdStyle}>
                    <button
                      style={btnStyle}
                      onClick={() => openEditModal(pkg.id)}
                    >
                      Edit
                    </button>
                    <button
                      style={btnDanger}
                      onClick={() => handleDelete(pkg.id)}
                      disabled={deletingId === pkg.id}
                    >
                      {deletingId === pkg.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div style={modalOverlayStyle} onClick={closeEditModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3
              style={{
                marginTop: 0,
                marginBottom: "12px",
                fontSize: "20px",
              }}
            >
              Edit Package #{editingPackage?.id}
            </h3>

            <label style={{ fontSize: "13px", color: "#4b5563" }}>
              Package Name
            </label>
            <input
              style={inputStyle}
              name="package_name"
              value={formData.package_name}
              onChange={handleFormChange}
            />

            <label style={{ fontSize: "13px", color: "#4b5563" }}>Type</label>
            <select
              style={inputStyle}
              name="type"
              value={formData.type}
              onChange={handleFormChange}
            >
              <option value="">Select type</option>
              <option value="paid">Paid</option>
              <option value="free">Free</option>
            </select>

            <label style={{ fontSize: "13px", color: "#4b5563" }}>
              Price Per Person
            </label>
            <input
              style={inputStyle}
              name="price_per_person"
              type="number"
              value={formData.price_per_person}
              onChange={handleFormChange}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
                gap: "8px",
              }}
            >
              <button
                style={{ ...btnStyle, background: "#6b7280" }}
                type="button"
                onClick={closeEditModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                style={btnStyle}
                onClick={handleUpdate}
                disabled={saving}
              >
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {addModalOpen && (
        <div style={modalOverlayStyle} onClick={closeAddModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3
              style={{
                marginTop: 0,
                marginBottom: "12px",
                fontSize: "20px",
              }}
            >
              Add Package (Conference #{selectedConference})
            </h3>

            <label style={{ fontSize: "13px", color: "#4b5563" }}>
              Package Name
            </label>
            <input
              style={inputStyle}
              name="package_name"
              value={addFormData.package_name}
              onChange={handleAddFormChange}
            />

            <label style={{ fontSize: "13px", color: "#4b5563" }}>Type</label>
            <select
              style={inputStyle}
              name="type"
              value={addFormData.type}
              onChange={handleAddFormChange}
            >
              <option value="">Select type</option>
              <option value="paid">Paid</option>
              <option value="free">Free</option>
            </select>

            <label style={{ fontSize: "13px", color: "#4b5563" }}>
              Price Per Person
            </label>
            <input
              style={inputStyle}
              name="price_per_person"
              type="number"
              value={addFormData.price_per_person}
              onChange={handleAddFormChange}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
                gap: "8px",
              }}
            >
              <button
                style={{ ...btnStyle, background: "#6b7280" }}
                type="button"
                onClick={closeAddModal}
                disabled={adding}
              >
                Cancel
              </button>
              <button
                style={{
                  ...btnStyle,
                  background:
                    "linear-gradient(135deg, #16a34a 0%, #16a34a 100%)",
                }}
                onClick={handleAdd}
                disabled={adding}
              >
                {adding ? "Saving..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}